import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    FormControl,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';
import { environment } from 'environments/environment';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { CopyOnDblclickDirective } from '@fuse/directives/copy-on-dblclick.directive';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { format } from 'sql-formatter';
import Prism from 'prismjs';
import 'prismjs/components/prism-sql';
import { PublicFunctionsService } from 'app/mock-api/common/services/functions.service';
import { SaiDialogHeaderComponent } from 'app/ui/sai-dialog-header/sai-dialog-header.component';

/** Same cap as formula dialog side preview. */
const TABLE_PREVIEW_MAX_ROWS = 100;

export interface AddKpiPromptDialogData {
    isExternal: boolean;
    tableId?: number | string;
    externalTableId?: number | string;
    lang?: string;
    previewHeaders?: string[];
    previewRows?: any[][];
}

interface KpiLogicItem {
    id?: number | string;
    level?: number | string;
    kpi_name: string;
    calculation: string;
    columns_used?: string[];
    description: string;
    userTableId?: number;
    externalTableId?: number;
}

/** Unsaved KPI from add-kpi-with-ai; includes validation flag from API. */
interface GeneratedKpiCandidate extends KpiLogicItem {
    is_valid?: boolean;
}

interface KpiValidationPreview {
    sql: string;
    rows: Record<string, unknown>[];
}

interface GeneratedKpiPayload {
    level: number;
    kpi_name: string;
    calculation: string;
    columns_used: string[];
    description: string;
}

@Component({
    selector: 'app-add-kpi-prompt-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SaiDialogHeaderComponent,
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        ClipboardModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        TranslocoModule,
        FuseAlertComponent,
        CopyOnDblclickDirective,
    ],
    templateUrl: './add-kpi-prompt-dialog.component.html',
    styleUrls: ['./add-kpi-prompt-dialog.component.scss'],
})
export class AddKpiPromptDialogComponent {
    promptCtrl = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
    });

    isGenerating = false;
    isSaving = false;
    isValidating = false;
    showAlert = false;
    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };

    generatedKpi: GeneratedKpiCandidate | null = null;
    validationPreview: KpiValidationPreview | null = null;
    highlightedValidationSql: SafeHtml = '';

    readonly previewHeaders: string[];
    readonly previewRows: any[][];

    get hasGeneratedKpi(): boolean {
        return !!this.generatedKpi;
    }

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: AddKpiPromptDialogData,
        private dialogRef: MatDialogRef<AddKpiPromptDialogComponent>,
        private http: HttpClient,
        private sanitizer: DomSanitizer,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef,
        private publicFunctions: PublicFunctionsService
    ) {
        this.previewHeaders = Array.isArray(data?.previewHeaders)
            ? data.previewHeaders.map(h => String(h))
            : [];
        this.previewRows = Array.isArray(data?.previewRows)
            ? data.previewRows.slice(0, TABLE_PREVIEW_MAX_ROWS)
            : [];
    }

    trackByHeaderIndex(index: number): number {
        return index;
    }

    trackByRowIndex(index: number): number {
        return index;
    }

    close(): void {
        this.dialogRef.close();
    }

    private mapKpiItem(item: any): GeneratedKpiCandidate | null {
        if (!item || typeof item !== 'object') return null;

        const id = item?.id ?? item?.kpi_id ?? item?.kpiId ?? undefined;
        const level =
            item?.level ?? item?.kpi_level ?? item?.kpiLevel ?? undefined;
        const kpi_name =
            item?.kpi_name ?? item?.kpiName ?? item?.name ?? '';
        const calculation =
            item?.calculation ?? item?.formula ?? item?.expression ?? '';
        const description =
            item?.description ?? item?.desc ?? item?.about ?? '';
        const columns_used = Array.isArray(item?.columns_used)
            ? item.columns_used.map((v: any) => String(v))
            : [];

        if (
            (kpi_name ?? '') === '' &&
            (calculation ?? '') === '' &&
            (description ?? '') === ''
        ) {
            return null;
        }

        const is_valid =
            typeof item?.is_valid === 'boolean' ? item.is_valid : undefined;

        const userTableId =
            item?.userTableId ?? item?.user_table_id ?? undefined;
        const externalTableId =
            item?.externalTableId ?? item?.external_table_id ?? undefined;

        return {
            id,
            level,
            kpi_name: String(kpi_name),
            calculation: String(calculation),
            columns_used,
            description: String(description),
            ...(is_valid !== undefined ? { is_valid } : {}),
            ...(userTableId != null ? { userTableId } : {}),
            ...(externalTableId != null ? { externalTableId } : {}),
        };
    }

    private extractGeneratedKpi(response: any): GeneratedKpiCandidate | null {
        const raw =
            response?.data?.generated_kpi ??
            response?.data?.data?.generated_kpi;
        return this.mapKpiItem(raw);
    }

    private extractSavedNewKpi(response: any): KpiLogicItem | null {
        const inner = response?.data?.data ?? response?.data;
        if (!inner || typeof inner !== 'object') return null;
        const mapped = this.mapKpiItem(inner);
        return mapped;
    }

    private getErrorMessage(error: any): string {
        try {
            const msg = error?.error ?? error?.message;
            if (typeof msg === 'string') {
                return JSON.parse(msg)?.message ?? msg;
            }
            return error?.error?.message ?? error?.message ?? 'Request failed';
        } catch {
            return error?.error?.message ?? error?.message ?? 'Request failed';
        }
    }

    private setInlineAlert(type: FuseAlertType, message: string): void {
        this.alert = { type, message };
        this.showAlert = true;
        this.cdr.markForCheck();
    }

    private formatSqlSafely(sql: string): string {
        const cleanSql = String(sql ?? '').trim();
        if (!cleanSql) return '';

        const dialects: string[] = ['postgresql', 'sql'];
        for (const language of dialects) {
            try {
                return format(cleanSql, {
                    language: language as any,
                    tabWidth: 2,
                    useTabs: false,
                    keywordCase: 'upper',
                }).replace(/^\s+/, '');
            } catch {
                // try next
            }
        }
        return cleanSql.replace(/^\s+/, '');
    }

    onSqlCopied(copied: boolean): void {
        if (!copied) return;
        this.snackBar.open('SQL query copied', 'Close', {
            duration: 2000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
        });
    }

    closeInlineAlert(): void {
        this.showAlert = false;
        this.cdr.markForCheck();
    }

    formatPreviewCellValue(value: unknown): string {
        return this.publicFunctions.formatPreviewNumericCell(value, 4);
    }

    async generate(): Promise<void> {
        if (this.promptCtrl.invalid) {
            this.promptCtrl.markAsTouched();
            return;
        }

        const prompt = this.promptCtrl.value.trim();
        if (!prompt) return;

        const { isExternal } = this.data;
        const tableId = this.data.tableId;
        const externalTableId = this.data.externalTableId;

        if (isExternal && (externalTableId == null || externalTableId === '')) {
            this.setInlineAlert('error', 'External table id is missing.');
            return;
        }
        if (!isExternal && (tableId == null || tableId === '')) {
            this.setInlineAlert('error', 'Table id is missing.');
            return;
        }

        this.isGenerating = true;
        this.showAlert = false;
        this.generatedKpi = null;
        this.validationPreview = null;
        this.highlightedValidationSql = '';
        this.cdr.markForCheck();

        try {
            const payload = {
                prompt,
                isExternal: !!isExternal,
                tableId: isExternal ? 0 : Number(tableId),
                externalTableId: isExternal ? Number(externalTableId) : 0,
            };

            const res = await firstValueFrom(
                this.http.post(
                    `${environment.apiLink}kpi-logic/add-kpi-with-ai`,
                    payload
                )
            );

            const generated = this.extractGeneratedKpi(res);
            if (!generated) {
                this.setInlineAlert(
                    'error',
                    'Generated KPI data was not found in API response.'
                );
                return;
            }
            this.generatedKpi = generated;
        } catch (e: any) {
            this.setInlineAlert('error', this.getErrorMessage(e));
        } finally {
            this.isGenerating = false;
            this.cdr.markForCheck();
        }
    }

    private buildValidationContextPayload(): {
        isExternal: boolean;
        tableId: number;
        externalTableId: number;
    } {
        const isExternal = !!this.data?.isExternal;
        return {
            isExternal,
            tableId: isExternal ? 0 : Number(this.data?.tableId ?? 0),
            externalTableId: isExternal
                ? Number(this.data?.externalTableId ?? 0)
                : 0,
        };
    }

    private toGeneratedKpiPayload(kpi: GeneratedKpiCandidate): GeneratedKpiPayload {
        return {
            level: Number(kpi.level ?? 0),
            kpi_name: String(kpi.kpi_name ?? ''),
            calculation: String(kpi.calculation ?? ''),
            columns_used: Array.isArray(kpi.columns_used)
                ? kpi.columns_used.map(v => String(v))
                : [],
            description: String(kpi.description ?? ''),
        };
    }

    async validateGeneratedKpi(): Promise<void> {
        if (!this.generatedKpi) {
            this.setInlineAlert(
                'error',
                'Generate a KPI first before validating.'
            );
            return;
        }

        const payload = {
            generated_kpi: this.toGeneratedKpiPayload(this.generatedKpi),
            ...this.buildValidationContextPayload(),
        };

        await this.executeValidationRequest(payload);
    }

    private async executeValidationRequest(payload: any): Promise<void> {
        this.isValidating = true;
        this.showAlert = false;
        this.validationPreview = null;
        this.cdr.markForCheck();
        try {
            const res: any = await firstValueFrom(
                this.http.post(
                    `${environment.apiLink}kpi-logic/validate-kpi`,
                    payload
                )
            );
            const sql = String(res?.data?.data?.sql ?? '');
            const rows = Array.isArray(res?.data?.data?.rows)
                ? (res.data.data.rows as Record<string, unknown>[])
                : [];

            this.validationPreview = { sql, rows };
            if (sql) {
                const pretty = this.formatSqlSafely(sql);
                const html = Prism.highlight(pretty, Prism.languages.sql, 'sql');
                this.highlightedValidationSql =
                    this.sanitizer.bypassSecurityTrustHtml(html);
            } else {
                this.highlightedValidationSql = '';
            }
            this.setInlineAlert('success', 'KPI validated successfully');
        } catch (e: any) {
            this.setInlineAlert('error', this.getErrorMessage(e));
        } finally {
            this.isValidating = false;
            this.cdr.markForCheck();
        }
    }

    async saveGeneratedNewKpi(): Promise<void> {
        if (!this.generatedKpi) {
            this.setInlineAlert('error', 'Generate a KPI first before saving.');
            return;
        }

        const { isExternal } = this.data;
        const tableId = this.data.tableId;
        const externalTableId = this.data.externalTableId;

        this.isSaving = true;
        this.showAlert = false;
        this.cdr.markForCheck();

        try {
            const payload = {
                is_valid: this.generatedKpi.is_valid !== false,
                level: Number(this.generatedKpi.level ?? 0),
                kpi_name: this.generatedKpi.kpi_name,
                calculation: this.generatedKpi.calculation,
                columns_used: this.generatedKpi.columns_used ?? [],
                description: this.generatedKpi.description,
                tableId: isExternal ? 0 : Number(tableId ?? 0),
                externalTableId: isExternal
                    ? Number(externalTableId ?? 0)
                    : 0,
                isExternal: !!isExternal,
            };

            const res = await firstValueFrom(
                this.http.post(
                    `${environment.apiLink}kpi-logic/save-generated-new-kpi`,
                    payload
                )
            );

            const saved = this.extractSavedNewKpi(res);
            if (!saved) {
                this.setInlineAlert(
                    'error',
                    'Saved KPI data was not found in API response.'
                );
                return;
            }

            this.dialogRef.close([saved]);
        } catch (e: any) {
            this.setInlineAlert('error', this.getErrorMessage(e));
        } finally {
            this.isSaving = false;
            this.cdr.markForCheck();
        }
    }
}
